import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import {
  Envelope, PaperPlaneTilt, Sparkle, Check, X, Trash,
  MagnifyingGlass, UploadSimple, ArrowRight, Leaf, Snowflake,
  Wrench, Buildings, ListChecks, CaretDown, CaretRight, Robot,
  Users,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminOfficeInbox — the AI office assistant that reads pasted
 * LawnGuru / MaintainX / AppFolio / ClickUp emails, classifies them,
 * fuzzy-matches to properties, and routes them into the right module.
 *
 * Route: /admin/office-inbox
 *
 * Two panes:
 *   1) Paste box → "Parse & Preview" → item cards → "Send to Queue"
 *      High-confidence LawnGuru visits auto-log; the rest queue.
 *   2) Review Queue: one-tap approve/adjust/dismiss per item.
 */
export default function AdminOfficeInbox() {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || localStorage.getItem("access_token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const { user } = useAuth();
  const isFounder = !!(user && (user.platform_role === "super_admin" || user.is_founder));

  const [pasteText, setPasteText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [previews, setPreviews] = useState([]);   // items from parse-batch
  const [sending, setSending] = useState(false);

  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState({ pending: 0, resolved: 0, dismissed: 0, by_source: {}, by_owner: {} });
  const [queueFilter, setQueueFilter] = useState("pending");
  const [scope, setScope] = useState("mine");  // "mine" | "team" (founder only)
  const [forwardAddr, setForwardAddr] = useState(null);
  const [setupInfo, setSetupInfo] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [alertCfg, setAlertCfg] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertBusy, setAlertBusy] = useState(false);

  useEffect(() => {
    axios.get(`${BASE}/api/office-inbox/forwarding-address`, auth())
      .then(r => setForwardAddr(r.data))
      .catch(() => {});
    axios.get(`${BASE}/api/office-inbox/setup-info`, auth())
      .then(r => setSetupInfo(r.data))
      .catch(() => {});
    axios.get(`${BASE}/api/office-inbox/alert-config`, auth())
      .then(r => setAlertCfg(r.data))
      .catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const loadQueue = () => {
    axios.get(`${BASE}/api/office-inbox/queue`, { ...auth(), params: { status: queueFilter, scope } })
      .then(r => setQueue(r.data.queue || []))
      .catch(() => {});
    axios.get(`${BASE}/api/office-inbox/stats`, { ...auth(), params: { scope } })
      .then(r => setStats(r.data || {}))
      .catch(() => {});
  };
  useEffect(() => { loadQueue(); /* eslint-disable-next-line */ }, [queueFilter, scope]);

  const parse = async () => {
    if (!pasteText.trim()) return toast.error("Paste one or more emails first");
    setParsing(true);
    try {
      const { data } = await axios.post(`${BASE}/api/office-inbox/parse-batch`,
        { text: pasteText }, auth());
      setPreviews((data.items || []).map(x => ({ ...x, _keep: true })));
      if ((data.items || []).length === 0) toast.error("Nothing parseable found");
      else toast.success(`Parsed ${data.items.length} email${data.items.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Parse failed"));
    } finally {
      setParsing(false);
    }
  };

  const send = async () => {
    const items = previews.filter(p => p._keep);
    if (!items.length) return toast.error("Nothing selected to send");
    setSending(true);
    try {
      const { data } = await axios.post(`${BASE}/api/office-inbox/queue`,
        { items, auto_commit_high_confidence: true }, auth());
      const auto = (data.auto_logged || []).length;
      const q = (data.queued || []).length;
      toast.success(`${auto ? `Auto-logged ${auto} · ` : ""}Queued ${q} for review`);
      setPreviews([]);
      setPasteText("");
      loadQueue();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Queue failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="admin-office-inbox-page">
        <div className="flex items-center gap-3">
          <Robot weight="fill" size={30} className="text-indigo-600"/>
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
              Office Inbox
            </h1>
            <div className="text-sm text-slate-600 font-medium mt-0.5">
              Paste LawnGuru · MaintainX · AppFolio · ClickUp emails — the assistant classifies, matches, and files them.
            </div>
          </div>
        </div>

        {/* Founder-only "My Inbox / Team Inbox" toggle */}
        {isFounder && (
          <div className="mt-4 flex items-center gap-2" data-testid="scope-toggle">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">View</div>
            <div className="inline-flex bg-slate-100 border-2 border-slate-200 p-0.5">
              <button
                onClick={() => setScope("mine")}
                data-testid="scope-mine"
                className={`px-3 py-1 text-[11px] font-black uppercase tracking-widest ${
                  scope === "mine" ? "bg-white text-slate-900 border border-slate-300" : "text-slate-600 hover:text-slate-900"
                }`}>
                My Inbox
              </button>
              <button
                onClick={() => setScope("team")}
                data-testid="scope-team"
                className={`px-3 py-1 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${
                  scope === "team" ? "bg-white text-slate-900 border border-slate-300" : "text-slate-600 hover:text-slate-900"
                }`}>
                <Users size={12}/> Team Inbox
              </button>
            </div>
            {scope === "team" && (
              <div className="text-[10px] text-slate-500 italic">Founder-only — everyone's queue across the workspace</div>
            )}
          </div>
        )}

        {/* Stat strip */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="office-inbox-stats">
          <StatChip label="Pending"   value={stats.pending}   color="amber"/>
          <StatChip label="Resolved"  value={stats.resolved}  color="emerald"/>
          <StatChip label="Dismissed" value={stats.dismissed} color="slate"/>
          <StatChip label="Sources"   value={Object.keys(stats.by_source || {}).length} color="indigo"/>
        </div>

        {/* Team-mode: pending backlog by owner */}
        {scope === "team" && stats.by_owner && Object.keys(stats.by_owner).length > 0 && (
          <div className="mt-3 border-2 border-slate-200 bg-white p-3" data-testid="team-by-owner">
            <div className="font-black uppercase text-[10px] tracking-widest text-slate-500 mb-2">
              Pending backlog by user
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_owner)
                .sort((a, b) => b[1] - a[1])
                .map(([email, count]) => (
                  <div key={email} className="inline-flex items-center gap-1.5 border border-slate-300 bg-slate-50 px-2 py-1"
                    data-testid={`owner-chip-${email}`}>
                    <Users size={10} className="text-slate-500"/>
                    <span className="text-xs font-mono">{email}</span>
                    <span className="text-[10px] font-black text-slate-700 bg-white border border-slate-300 px-1">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Forwarding address panel — Phase 2 */}
        {forwardAddr && (
          <div className="mt-4 border-2 border-emerald-500 bg-emerald-50 p-3" data-testid="forwarding-panel">
            <div className="flex items-center gap-2 mb-1">
              <PaperPlaneTilt weight="fill" size={16} className="text-emerald-600"/>
              <div className="font-black uppercase text-[11px] tracking-widest text-emerald-900">
                Your Personal Assistant Address
              </div>
              <div className={`ml-auto inline-flex items-center gap-1 border px-1.5 py-0.5 font-black uppercase text-[9px] tracking-widest ${
                setupInfo?.activated
                  ? "border-emerald-400 bg-white text-emerald-800"
                  : "border-amber-400 bg-white text-amber-800"
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full inline-block ${
                  setupInfo?.activated ? "bg-emerald-500" : "bg-amber-500"
                }`}/>
                {setupInfo?.activated ? "Live" : "Not receiving yet"}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="flex-1 min-w-0 truncate bg-white border-2 border-emerald-300 px-2 py-1.5 font-mono text-sm text-slate-900"
                data-testid="forwarding-address">
                {forwardAddr.address}
              </code>
              <button
                onClick={() => { navigator.clipboard.writeText(forwardAddr.address); toast.success("Copied!"); }}
                data-testid="copy-forwarding-btn"
                className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest">
                Copy
              </button>
            </div>
            <div className="mt-2 text-xs text-emerald-900/80">
              This address is <b>personal to you</b>. Forward matching Gmail here and only <b>you</b> see the results. Ciarra, Grace, Mykee each get their own separate address if they open this page.
            </div>

            <button
              onClick={() => setSetupOpen(v => !v)}
              data-testid="toggle-setup-instructions"
              className="mt-3 text-[11px] font-black uppercase tracking-widest text-emerald-800 hover:text-emerald-900 flex items-center gap-1">
              {setupOpen ? <CaretDown size={12}/> : <CaretRight size={12}/>}
              {setupInfo?.activated ? "View setup steps" : "🚀 Activate live forwarding — 3 steps"}
            </button>

            {setupOpen && setupInfo && (
              <div className="mt-3 space-y-3 text-xs" data-testid="setup-instructions">
                {/* Step 1 — DNS */}
                <div className="border-2 border-emerald-300 bg-white p-3">
                  <div className="font-black uppercase text-[10px] tracking-widest text-emerald-800 mb-1">
                    Step 1 — DNS MX Record (one-time, on your domain registrar)
                  </div>
                  <div className="text-slate-700 mb-2">
                    On whoever hosts DNS for <b>fieldradar.pro</b> (Cloudflare, Namecheap, etc.), add an MX record on the <code className="bg-slate-100 px-1">{setupInfo.dns_mx_hostname}</code> subdomain:
                  </div>
                  <div className="grid grid-cols-3 gap-1 text-[11px] font-mono border border-slate-200">
                    <div className="bg-slate-100 p-1.5 font-black">Host</div>
                    <div className="bg-slate-100 p-1.5 font-black">Value</div>
                    <div className="bg-slate-100 p-1.5 font-black">Priority</div>
                    <div className="p-1.5">{setupInfo.dns_mx_hostname}</div>
                    <div className="p-1.5">{setupInfo.dns_mx_record}</div>
                    <div className="p-1.5">{setupInfo.dns_mx_priority}</div>
                  </div>
                </div>

                {/* Step 2 — SendGrid */}
                <div className="border-2 border-emerald-300 bg-white p-3">
                  <div className="font-black uppercase text-[10px] tracking-widest text-emerald-800 mb-1">
                    Step 2 — SendGrid Inbound Parse (one-time)
                  </div>
                  <div className="text-slate-700 mb-2">
                    In your SendGrid account → <b>Settings → Inbound Parse → Add Host & URL</b>. Paste this destination URL:
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 truncate bg-slate-100 px-2 py-1.5 font-mono text-[11px] text-slate-900">
                      https://fieldradar.pro{setupInfo.sendgrid_webhook_path}
                    </code>
                    <button
                      onClick={() => { navigator.clipboard.writeText(`https://fieldradar.pro${setupInfo.sendgrid_webhook_path}`); toast.success("URL copied"); }}
                      data-testid="copy-webhook-btn"
                      className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest">
                      Copy
                    </button>
                  </div>
                  <div className="mt-2 text-[10px] text-slate-500">
                    Receiving domain: <b>{setupInfo.dns_mx_hostname}</b> · Check "POST the raw, full MIME message" is <b>off</b>.
                  </div>
                </div>

                {/* Step 3 — Gmail */}
                <div className="border-2 border-emerald-300 bg-white p-3">
                  <div className="font-black uppercase text-[10px] tracking-widest text-emerald-800 mb-1">
                    Step 3 — Gmail Filter (per source you want to auto-file)
                  </div>
                  <div className="text-slate-700 mb-2">
                    In Gmail → <b>Settings → See all settings → Filters and Blocked Addresses → Create new filter</b>. For each source (LawnGuru / MaintainX / AppFolio / ClickUp), match by sender email, then check <b>"Forward it to"</b> and paste your personal address:
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 truncate bg-slate-100 px-2 py-1.5 font-mono text-[11px] text-slate-900">
                      {setupInfo.gmail_forward_address}
                    </code>
                    <button
                      onClick={() => { navigator.clipboard.writeText(setupInfo.gmail_forward_address); toast.success("Address copied"); }}
                      data-testid="copy-gmail-forward-btn"
                      className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest">
                      Copy
                    </button>
                  </div>
                  <div className="mt-2 text-[10px] text-slate-500">
                    Gmail will send you a verification email at this address the first time. It shows up right here in the queue below — just approve it.
                  </div>
                </div>

                <div className="text-[10px] text-emerald-900/60 italic">
                  Once all three steps are done, any matching email you receive in Gmail will silently forward here — auto-log if we're confident, otherwise land in your review queue.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Backlog alert config */}
        {alertCfg && (
          <div className="mt-3 border-2 border-amber-400 bg-amber-50 p-3" data-testid="alert-config-panel">
            <button
              onClick={() => setAlertOpen(v => !v)}
              data-testid="alert-config-toggle"
              className="w-full flex items-center gap-2 text-left">
              <Sparkle weight="fill" size={14} className="text-amber-600"/>
              <div className="font-black uppercase text-[11px] tracking-widest text-amber-900">
                Backlog Alerts
              </div>
              <div className="ml-2 inline-flex items-center gap-1 border border-amber-400 bg-white px-1.5 py-0.5 font-black uppercase text-[9px] tracking-widest text-amber-800">
                {alertCfg.enabled ? `On · alert at ${alertCfg.threshold}+ pending` : "Off"}
              </div>
              <div className="ml-auto">{alertOpen ? <CaretDown size={12}/> : <CaretRight size={12}/>}</div>
            </button>

            {alertOpen && (
              <div className="mt-3 space-y-2 text-xs" data-testid="alert-config-body">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!alertCfg.enabled}
                    onChange={async (e) => {
                      const next = { ...alertCfg, enabled: e.target.checked };
                      setAlertCfg(next);
                      await axios.post(`${BASE}/api/office-inbox/alert-config`,
                        { enabled: e.target.checked }, auth()).catch(() => {});
                    }}
                    data-testid="alert-enabled"/>
                  <span>Email me when my pending queue crosses the threshold</span>
                </label>

                {/* Read-only mode — pure observer */}
                <label className="flex items-start gap-2 border-t-2 border-amber-200 pt-2 mt-1">
                  <input
                    type="checkbox"
                    checked={!!alertCfg.read_only}
                    onChange={async (e) => {
                      const next = { ...alertCfg, read_only: e.target.checked };
                      setAlertCfg(next);
                      await axios.post(`${BASE}/api/office-inbox/alert-config`,
                        { read_only: e.target.checked }, auth()).catch(() => {});
                      toast.success(e.target.checked
                        ? "Read-only ON — nothing auto-logs; everything queues for review"
                        : "Read-only OFF — LawnGuru high-confidence visits auto-log again");
                    }}
                    data-testid="alert-readonly"
                    className="mt-0.5"/>
                  <span>
                    <b>Read-only mode</b> — AI reads and queues everything, but <u>never auto-logs</u>. Every email (including confident LawnGuru visits) sits in your queue until you tap Approve. Pure observer mode.
                  </span>
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] font-black uppercase text-slate-500 mb-0.5">Threshold</div>
                    <input
                      type="number" min="1" max="1000"
                      value={alertCfg.threshold}
                      onChange={e => setAlertCfg({ ...alertCfg, threshold: parseInt(e.target.value, 10) || 10 })}
                      onBlur={async (e) => {
                        await axios.post(`${BASE}/api/office-inbox/alert-config`,
                          { threshold: parseInt(e.target.value, 10) || 10 }, auth()).catch(() => {});
                      }}
                      data-testid="alert-threshold"
                      className="w-full h-8 px-2 border-2 border-slate-300 text-xs font-mono"/>
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase text-slate-500 mb-0.5">Cooldown (hrs)</div>
                    <input
                      type="number" min="0.5" max="168" step="0.5"
                      value={alertCfg.cooldown_hours}
                      onChange={e => setAlertCfg({ ...alertCfg, cooldown_hours: parseFloat(e.target.value) || 6 })}
                      onBlur={async (e) => {
                        await axios.post(`${BASE}/api/office-inbox/alert-config`,
                          { cooldown_hours: parseFloat(e.target.value) || 6 }, auth()).catch(() => {});
                      }}
                      data-testid="alert-cooldown"
                      className="w-full h-8 px-2 border-2 border-slate-300 text-xs font-mono"/>
                  </div>
                </div>

                <div>
                  <div className="text-[10px] font-black uppercase text-slate-500 mb-0.5">
                    Deliver to (optional override — default is your login email)
                  </div>
                  <input
                    type="email"
                    placeholder="you@yourdomain.com"
                    value={alertCfg.email_override || ""}
                    onChange={e => setAlertCfg({ ...alertCfg, email_override: e.target.value })}
                    onBlur={async (e) => {
                      await axios.post(`${BASE}/api/office-inbox/alert-config`,
                        { email_override: e.target.value }, auth()).catch(() => {});
                    }}
                    data-testid="alert-email-override"
                    className="w-full h-8 px-2 border-2 border-slate-300 text-xs font-mono"/>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      setAlertBusy(true);
                      try {
                        const { data } = await axios.post(
                          `${BASE}/api/office-inbox/alert-config/test`, {}, auth());
                        toast.success(`Test alert sent to ${data.sent_to}`);
                      } catch (e) {
                        toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Test send failed"));
                      } finally { setAlertBusy(false); }
                    }}
                    disabled={alertBusy || !alertCfg.enabled}
                    data-testid="alert-test-send"
                    className="h-8 px-3 bg-amber-600 hover:bg-amber-700 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
                    {alertBusy ? "Sending…" : "Send test alert"}
                  </button>
                  {alertCfg.last_alerted_at && (
                    <div className="text-[10px] text-slate-500">
                      Last alerted: {new Date(alertCfg.last_alerted_at).toLocaleString()}
                      {alertCfg.last_alerted_pending ? ` · at ${alertCfg.last_alerted_pending} pending` : ""}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Paste + parse */}
        <div className="mt-4 border-2 border-slate-200 bg-white" data-testid="paste-panel">
          <div className="px-3 py-2 bg-slate-50 border-b-2 border-slate-200 flex items-center gap-2">
            <Envelope weight="fill" size={16} className="text-indigo-500"/>
            <div className="font-black uppercase text-[11px] tracking-widest">Paste Email(s)</div>
            <div className="ml-auto text-[10px] text-slate-500 font-medium">
              Bulk paste OK — Gmail separators are auto-detected
            </div>
          </div>
          <textarea
            value={pasteText}
            onChange={e => setPasteText(e.target.value)}
            data-testid="paste-textarea"
            placeholder={"Paste the full email body(ies) here…\n\nExample:\n---\nSubject: Weekly Mowing at 5346 Chew Ave has been completed\nFrom: notify@lawnguru.co\n\nPaul completed your job! ..."}
            rows={8}
            className="w-full p-3 text-sm font-mono outline-none resize-y"
          />
          <div className="px-3 py-2 border-t-2 border-slate-200 flex items-center gap-2 bg-slate-50">
            <button onClick={parse} disabled={parsing || !pasteText.trim()}
              data-testid="parse-btn"
              className="h-9 px-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
              <Sparkle size={14}/> {parsing ? "Parsing…" : "Parse & Preview"}
            </button>
            {pasteText && (
              <button onClick={() => { setPasteText(""); setPreviews([]); }}
                data-testid="paste-clear-btn"
                className="h-9 px-3 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest">
                Clear
              </button>
            )}
            <div className="ml-auto text-[10px] text-slate-500 font-medium">
              {previews.length ? `${previews.length} previewed · ${previews.filter(p => p._keep).length} selected` : ""}
            </div>
          </div>
        </div>

        {/* Preview cards */}
        {previews.length > 0 && (
          <div className="mt-4 space-y-3" data-testid="preview-list">
            {previews.map((p, i) => (
              <PreviewCard key={p.preview_id} item={p}
                onToggle={() => setPreviews(prev => prev.map((x, idx) => idx === i ? { ...x, _keep: !x._keep } : x))}
                onEdit={(patch) => setPreviews(prev => prev.map((x, idx) => idx === i ? { ...x, ...patch } : x))}
              />
            ))}
            <div className="sticky bottom-3 z-10 flex items-center gap-2 border-2 border-slate-900 bg-white p-3 shadow-lg">
              <div className="text-xs font-black uppercase tracking-widest">
                {previews.filter(p => p._keep).length} selected — high-confidence LawnGuru will auto-log
              </div>
              <button onClick={send} disabled={sending || !previews.some(p => p._keep)}
                data-testid="send-to-queue-btn"
                className="ml-auto h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                <PaperPlaneTilt size={14}/> {sending ? "Sending…" : "Send to Queue"}
              </button>
            </div>
          </div>
        )}

        {/* Queue */}
        <div className="mt-6 flex items-center gap-2">
          <ListChecks size={18} weight="bold" className="text-slate-700"/>
          <div className="font-black uppercase text-sm tracking-widest">Review Queue</div>
          <div className="ml-auto flex items-center gap-1">
            {["pending", "resolved", "dismissed", "all"].map(f => (
              <button key={f} onClick={() => setQueueFilter(f)}
                data-testid={`queue-filter-${f}`}
                className={`h-8 px-2 font-black uppercase text-[10px] tracking-widest border-2 ${
                  queueFilter === f
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-300 text-slate-600 hover:bg-slate-100"
                }`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        {queue.length === 0 && (
          <div className="mt-3 p-6 border-2 border-dashed border-slate-300 text-center text-sm text-slate-500 font-medium" data-testid="queue-empty">
            No {queueFilter} items. Paste an email above to get started.
          </div>
        )}
        {queue.length > 0 && (
          <div className="mt-3 space-y-3" data-testid="queue-list">
            {queue.map(row => (
              <QueueCard key={row.id} row={row} onChanged={loadQueue}/>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}


function StatChip({ label, value, color }) {
  const map = {
    amber:   "border-amber-300   bg-amber-50   text-amber-900",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-900",
    slate:   "border-slate-300   bg-slate-50   text-slate-700",
    indigo:  "border-indigo-300  bg-indigo-50  text-indigo-900",
  };
  return (
    <div className={`border-2 ${map[color]} p-2`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-black">{value ?? 0}</div>
    </div>
  );
}


const SOURCE_META = {
  lawnguru:  { label: "LawnGuru",  icon: Leaf,      color: "emerald" },
  maintainx: { label: "MaintainX", icon: Wrench,    color: "orange" },
  appfolio:  { label: "AppFolio",  icon: Buildings, color: "sky" },
  clickup:   { label: "ClickUp",   icon: ListChecks, color: "violet" },
  unknown:   { label: "Unknown",   icon: Envelope,  color: "slate" },
};


function SourceBadge({ source, confidence }) {
  const meta = SOURCE_META[source] || SOURCE_META.unknown;
  const Icon = meta.icon;
  const bg = {
    emerald: "bg-emerald-100 text-emerald-900 border-emerald-300",
    orange:  "bg-orange-100 text-orange-900 border-orange-300",
    sky:     "bg-sky-100 text-sky-900 border-sky-300",
    violet:  "bg-violet-100 text-violet-900 border-violet-300",
    slate:   "bg-slate-100 text-slate-700 border-slate-300",
  }[meta.color];
  return (
    <div className={`inline-flex items-center gap-1 border-2 ${bg} px-2 py-0.5 font-black uppercase text-[10px] tracking-widest`}>
      <Icon size={12} weight="fill"/> {meta.label}
      {typeof confidence === "number" && (
        <span className="opacity-70">· {Math.round(confidence * 100)}%</span>
      )}
    </div>
  );
}


function PreviewCard({ item, onToggle, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [pickedMatchIdx, setPickedMatchIdx] = useState(0);
  const [note, setNote] = useState("");
  const p = item.parsed || {};
  const matches = item.matches || [];
  const isAuto = item.auto_action === "log_visit" && item.overall_confidence >= 0.75;

  return (
    <div className={`border-2 ${item._keep ? "border-slate-900" : "border-slate-200 opacity-60"} bg-white`}
      data-testid={`preview-card-${item.preview_id}`}>
      <div className="flex items-center gap-2 p-3 border-b-2 border-slate-100">
        <input type="checkbox" checked={!!item._keep} onChange={onToggle}
          data-testid={`preview-keep-${item.preview_id}`}
          className="w-4 h-4 accent-slate-900"/>
        <SourceBadge source={item.source} confidence={item.overall_confidence}/>
        {isAuto && (
          <div className="inline-flex items-center gap-1 border-2 border-emerald-500 bg-emerald-50 text-emerald-900 px-2 py-0.5 font-black uppercase text-[10px] tracking-widest">
            <Sparkle size={12} weight="fill"/> Will auto-log
          </div>
        )}
        <button onClick={() => setExpanded(v => !v)}
          data-testid={`preview-expand-${item.preview_id}`}
          className="ml-auto h-7 px-2 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          {expanded ? <CaretDown size={12}/> : <CaretRight size={12}/>} {expanded ? "Hide" : "Details"}
        </button>
      </div>

      <div className="p-3 text-sm">
        <div className="font-black">{item.subject || "(no subject)"}</div>
        <div className="text-xs text-slate-500 mt-0.5">{item.from || "—"} · {item.date || ""}</div>

        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {p.address && <Field label="Address"     value={p.address}/>}
          {p.tech &&    <Field label="Tech"        value={p.tech}/>}
          {p.service_type && <Field label="Service" value={p.service_type}/>}
          {p.work_order_number && <Field label="WO #" value={p.work_order_number}/>}
          {p.status &&  <Field label="Status"      value={p.status}/>}
          {p.assignee && <Field label="Assignee"   value={p.assignee}/>}
          {p.tenant_name && <Field label="Tenant"  value={p.tenant_name}/>}
          {p.unit &&    <Field label="Unit"        value={p.unit}/>}
          {p.balance_due != null && <Field label="Balance Due" value={`$${p.balance_due}`}/>}
          {p.task_title && <Field label="Task"     value={p.task_title}/>}
        </div>

        {matches.length > 0 ? (
          <div className="mt-3 border-t-2 border-dashed border-slate-200 pt-2">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">
              <MagnifyingGlass size={12} className="inline mr-1"/> Closest matches
            </div>
            <div className="flex flex-wrap gap-1">
              {matches.map((m, mi) => (
                <button key={mi}
                  onClick={() => setPickedMatchIdx(mi)}
                  data-testid={`match-chip-${item.preview_id}-${mi}`}
                  className={`text-xs border-2 px-2 py-1 font-medium ${
                    mi === pickedMatchIdx
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-300 hover:bg-slate-100"
                  }`}>
                  {m.address}
                  <span className="opacity-70 ml-1">· {Math.round((m.score || 0) * 100)}%</span>
                  {m.kind === "seasonal_service" && <span className="opacity-70 ml-1">· {m.service_type}</span>}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-3 text-xs text-amber-700 font-bold">
            No match found — will queue for review.
          </div>
        )}

        {expanded && (
          <>
            <div className="mt-3 border-t-2 border-dashed border-slate-200 pt-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Optional note (kept with the log entry)</label>
              <textarea rows={2} value={note}
                onChange={e => { setNote(e.target.value); onEdit({ note: e.target.value }); }}
                data-testid={`preview-note-${item.preview_id}`}
                className="w-full mt-1 p-2 text-xs border-2 border-slate-300 outline-none"
                placeholder="e.g., Gate was locked — mowed front only."/>
            </div>
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer font-black uppercase text-[10px] tracking-widest text-slate-500">Raw email excerpt</summary>
              <pre className="mt-1 p-2 bg-slate-50 border border-slate-200 whitespace-pre-wrap text-[11px]">{item.raw_excerpt}</pre>
            </details>
          </>
        )}
      </div>
    </div>
  );
}


function Field({ label, value }) {
  return (
    <div className="border border-slate-200 bg-slate-50 px-2 py-1">
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-medium text-slate-800 break-words">{value}</div>
    </div>
  );
}


function QueueCard({ row, onChanged }) {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const [expanded, setExpanded] = useState(row.status === "pending");
  const [pickedIdx, setPickedIdx] = useState(0);
  const [note, setNote] = useState("");
  const [photoPath, setPhotoPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const p = row.parsed || {};
  const matches = row.matches || [];
  const pending = row.status === "pending";

  const uploadPhoto = async (file) => {
    if (!file) return;
    const top = matches[pickedIdx];
    const serviceIdForUpload = top?.service_id
      || matches.find(m => m.kind === "seasonal_service")?.service_id;
    if (!serviceIdForUpload) {
      return toast.error("Pick a seasonal service match first (for upload path)");
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(
        `${BASE}/api/seasonal-services/${serviceIdForUpload}/visit-photo`,
        form,
        { ...auth(), headers: { ...auth().headers, "Content-Type": "multipart/form-data" } }
      );
      setPhotoPath(data.path || "");
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const approve = async () => {
    const top = matches[pickedIdx];
    if (!top) return toast.error("Pick a match first");
    setSaving(true);
    try {
      const body = { note, photo_path: photoPath || undefined };
      if (top.kind === "seasonal_service") body.match_service_id = top.service_id;
      else body.match_property_id = top.property_id;
      const { data } = await axios.post(
        `${BASE}/api/office-inbox/queue/${row.id}/approve`,
        body, auth()
      );
      toast.success(`Filed to ${data.resolved_link?.kind?.replace("_", " ") || "record"}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Approve failed"));
    } finally {
      setSaving(false);
    }
  };

  const dismiss = async () => {
    if (!window.confirm("Dismiss this email?")) return;
    try {
      await axios.post(`${BASE}/api/office-inbox/queue/${row.id}/dismiss`, {}, auth());
      toast.success("Dismissed");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Dismiss failed"));
    }
  };

  return (
    <div className={`border-2 ${pending ? "border-slate-300" : "border-slate-200 opacity-70"} bg-white`}
      data-testid={`queue-card-${row.id}`}>
      <div className="flex items-center gap-2 p-3 border-b-2 border-slate-100">
        <SourceBadge source={row.source} confidence={row.overall_confidence}/>
        <div className="text-xs font-medium text-slate-700 truncate">
          {row.subject || "(no subject)"}
        </div>
        {row.owner_email && (
          <div className="hidden sm:inline-flex items-center gap-1 border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-600"
            title="Which user's inbox this landed in"
            data-testid={`queue-owner-${row.id}`}>
            <Users size={10}/> {row.owner_email}
          </div>
        )}
        <div className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-400">
          {row.status}
        </div>
        <button onClick={() => setExpanded(v => !v)}
          data-testid={`queue-expand-${row.id}`}
          className="h-7 px-2 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          {expanded ? <CaretDown size={12}/> : <CaretRight size={12}/>}
        </button>
      </div>

      {expanded && (
        <div className="p-3 text-sm space-y-3">
          <div className="text-xs text-slate-500">{row.from || "—"} · {row.date || ""}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            {p.address && <Field label="Address" value={p.address}/>}
            {p.tech && <Field label="Tech" value={p.tech}/>}
            {p.service_type && <Field label="Service" value={p.service_type}/>}
            {p.work_order_number && <Field label="WO #" value={p.work_order_number}/>}
            {p.status && <Field label="Status" value={p.status}/>}
            {p.assignee && <Field label="Assignee" value={p.assignee}/>}
            {p.tenant_name && <Field label="Tenant" value={p.tenant_name}/>}
            {p.balance_due != null && <Field label="Balance" value={`$${p.balance_due}`}/>}
            {p.task_title && <Field label="Task" value={p.task_title}/>}
          </div>

          {pending && matches.length > 0 && (
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">
                Pick the right record
              </div>
              <div className="flex flex-wrap gap-1">
                {matches.map((m, mi) => (
                  <button key={mi} onClick={() => setPickedIdx(mi)}
                    data-testid={`queue-match-${row.id}-${mi}`}
                    className={`text-xs border-2 px-2 py-1 font-medium ${
                      mi === pickedIdx
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 hover:bg-slate-100"
                    }`}>
                    {m.address}
                    <span className="opacity-70 ml-1">· {Math.round((m.score || 0) * 100)}%</span>
                    {m.kind === "seasonal_service" && <span className="opacity-70 ml-1">· {m.service_type}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {pending && matches.length === 0 && (
            <div className="text-xs text-amber-700 font-bold">
              No fuzzy match — approve won't work until you add the address to a seasonal route or property first.
            </div>
          )}

          {pending && (
            <>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Note</label>
                <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
                  data-testid={`queue-note-${row.id}`}
                  className="w-full mt-1 p-2 text-xs border-2 border-slate-300 outline-none"
                  placeholder="Optional note attached to the log entry"/>
              </div>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*"
                  data-testid={`queue-file-${row.id}`}
                  onChange={e => uploadPhoto(e.target.files?.[0])}
                  className="hidden"/>
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid={`queue-photo-btn-${row.id}`}
                  className="h-9 px-3 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                  <UploadSimple size={14}/> {uploading ? "Uploading…" : photoPath ? "Photo attached" : "Attach photo"}
                </button>
                <button onClick={dismiss} disabled={saving}
                  data-testid={`queue-dismiss-${row.id}`}
                  className="ml-auto h-9 px-3 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                  <Trash size={14}/> Dismiss
                </button>
                <button onClick={approve} disabled={saving || matches.length === 0}
                  data-testid={`queue-approve-${row.id}`}
                  className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                  <Check size={14}/> {saving ? "Filing…" : "Approve & File"}
                </button>
              </div>
            </>
          )}

          {!pending && row.resolved_link && (
            <div className="text-xs text-slate-600 border-t-2 border-dashed border-slate-200 pt-2">
              <span className="font-black uppercase text-[10px] tracking-widest">Filed to:</span>{" "}
              {row.resolved_link.kind?.replace("_", " ")} — {row.resolved_link.address || ""}
            </div>
          )}

          <details className="text-xs">
            <summary className="cursor-pointer font-black uppercase text-[10px] tracking-widest text-slate-500">Raw excerpt</summary>
            <pre className="mt-1 p-2 bg-slate-50 border border-slate-200 whitespace-pre-wrap text-[11px]">{row.raw_excerpt}</pre>
          </details>
        </div>
      )}
    </div>
  );
}
